// A report covering every office (Bancamía's Bantotal exports, ~4.400 rows for 140 offices),
// opened on the rows of this closing's office.
//
// The backend reads the file and answers the header and those rows (`getCierreDocumentPreview`),
// a few kilobytes instead of the whole workbook Microsoft's viewer used to fetch and draw. "Ver
// completo" still opens that viewer — `vistaCompleta`, which the dialog builds exactly as before.
//
// Anything that is not a preview falls back to `vistaCompleta` on its own: a document whose
// declaration has no `office_column:` (every Davivienda document), a backend that predates the route
// (the two repos deploy separately), or a call that failed. So the worst case is the old screen.
//
// Cells are rendered through React one by one, like `VistaHojaDeCalculo`, never as HTML.

import { useEffect, useState } from "react";
import { getCierreDocumentPreview } from "../../../ApiCalls/mantisBusiness";
import { descripcionDeFiltro } from "../../../utils/cierreDocuments";

// `contain: inline-size` keeps a wide sheet from widening the dialog's grid cell, which would push
// "Ver completo" off to the right; the table scrolls inside instead. Set here rather than on the
// dialog's container so a document with no preview (every Davivienda one) lays out as before.
const CONTENEDOR = "flex w-full flex-col gap-1.5 [contain:inline-size]";

const BOTON =
  "shrink-0 rounded-[6px] border border-brinks-border bg-card px-2 py-0.5 text-[11px] font-medium text-brinks-foreground hover:bg-brinks-surface";

export function VistaPreviaHoja({ token, mantisBusinessId, closingId, documento, vistaCompleta }) {
  const [preview, setPreview] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [verCompleto, setVerCompleto] = useState(false);
  const documentoId = documento?.id;

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    setPreview(null);
    setVerCompleto(false);
    getCierreDocumentPreview(token, mantisBusinessId, closingId, documentoId)
      .then((res) => {
        if (vigente) setPreview(res?.success && res.data?.available ? res.data : null);
      })
      .catch(() => {
        if (vigente) setPreview(null);
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, [token, mantisBusinessId, closingId, documentoId]);

  if (cargando) {
    return <p className="p-6 text-center text-[12.5px] text-brinks-muted-foreground">Abriendo hoja de cálculo…</p>;
  }

  if (!preview) return vistaCompleta;

  if (verCompleto) {
    return (
      <div className={CONTENEDOR}>
        <div className="flex items-center justify-end">
          <button type="button" className={BOTON} onClick={() => setVerCompleto(false)}>
            Ver solo la oficina
          </button>
        </div>
        {vistaCompleta}
      </div>
    );
  }

  const filas = [...preview.header, ...preview.rows];
  const columnas = filas.reduce((max, fila) => Math.max(max, fila.cells.length), 0);
  const encabezado = new Set(preview.header.map((fila) => fila.rowNumber));

  return (
    <div className={CONTENEDOR}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[11.5px] text-brinks-muted-foreground" title={preview.sheet}>
          {descripcionDeFiltro(preview.filter)}
        </span>
        <button type="button" className={BOTON} onClick={() => setVerCompleto(true)}>
          Ver completo
        </button>
      </div>
      <div className="max-h-[54vh] w-full overflow-auto rounded-[6px] border bg-card">
        <table className="min-w-full border-collapse text-[11px]">
          <tbody>
            {filas.map((fila) => (
              <tr
                key={fila.rowNumber}
                className={`border-b border-brinks-border ${encabezado.has(fila.rowNumber) ? "bg-brinks-surface font-semibold" : ""}`}
              >
                <td className="sticky left-0 bg-brinks-surface px-1.5 text-right text-brinks-muted-foreground">
                  {fila.rowNumber}
                </td>
                {Array.from({ length: columnas }, (_, j) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <td key={j} className="whitespace-nowrap border-l border-brinks-border px-1.5 py-0.5">
                    {fila.cells[j] ?? ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {preview.rows.length === 0 ? (
        <p className="text-[11.5px] text-brinks-muted-foreground">
          El archivo no tiene filas con el código de esta oficina. Usa «Ver completo» para revisarlo
          entero.
        </p>
      ) : null}
      {preview.matched > preview.rows.length ? (
        <p className="text-[11.5px] text-brinks-muted-foreground">
          Mostrando {preview.rows.length} de {preview.matched} filas.
        </p>
      ) : null}
    </div>
  );
}
